import { Link } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { useApiQuery } from '../api/hooks.js';
import {
  getMailcomInventory,
  getRecentActivity,
  type MailcomInventoryResponse,
  type RecentActivityResponse,
  type WireActivityEntry,
  type WireInventoryProjection,
} from '../api/endpoints.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IdMono,
  LoadingState,
  PageHead,
  RolePill,
  StatCard,
} from '../ui/index.js';
import type { AdminRole } from '../ui/index.js';
import {
  ACTIVITY_KIND_LABEL,
  ago,
  iconForActivityKind,
  toneForActivityKind,
} from '../i18n/activity-kinds.js';

function formatRelative(msRemaining: number | null): string {
  if (msRemaining === null) return '—';
  if (msRemaining <= 0) return 'süresi doldu';
  const totalSeconds = Math.floor(msRemaining / 1000);
  if (totalSeconds < 60) return `${totalSeconds} sn kaldı`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes} dk kaldı`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin === 0 ? `${hours} sa kaldı` : `${hours} sa ${remMin} dk kaldı`;
}

function sumAvailable(p: readonly WireInventoryProjection[]): number {
  return p.reduce((a, x) => a + x.availableCount, 0);
}
function sumLeased(p: readonly WireInventoryProjection[]): number {
  return p.reduce((a, x) => a + x.leasedCount + x.reservedCount, 0);
}
function sumTotal(p: readonly WireInventoryProjection[]): number {
  return p.reduce((a, x) => a + x.totalImported, 0);
}

export function DashboardPage(): JSX.Element {
  const session = useSession();
  const canProvider = useCan('provider:inspect');
  const canActivity = useCan('activity:read');
  const canMaintenance = useCan('maintenance:view');
  const canUserList = useCan('user:list');
  const canOrderInspect = useCan('order:inspect');
  const canFinancial = useCan('financial:review');

  const inventoryQuery = useApiQuery<MailcomInventoryResponse>(
    (o) => getMailcomInventory(o),
    [],
  );
  const activityQuery = useApiQuery<RecentActivityResponse>(
    (o) => getRecentActivity(o, 12),
    [],
  );

  const inventory = inventoryQuery.kind === 'ok' ? inventoryQuery.data.projections : [];
  const totalAvailable = sumAvailable(inventory);
  const totalLeased = sumLeased(inventory);
  const totalImported = sumTotal(inventory);

  const expiresAt = session.expiresAt;
  const msLeft = expiresAt ? expiresAt.getTime() - Date.now() : null;
  const adminRole: AdminRole = (session.role ?? 'admin') as AdminRole;

  const fmt = (n: number): string => (canProvider ? n.toLocaleString('tr-TR') : '—');

  // Quick-access list filtered by capability
  const quickActions = [
    { I: Icon.Users, t: 'Kullanıcı ara', to: '/users', show: canUserList },
    { I: Icon.Inbox, t: 'Sipariş incele', to: '/orders', show: canOrderInspect },
    { I: Icon.Server, t: 'mail.com Yönetimi', to: '/mailcom-control-plane', show: canProvider },
    { I: Icon.Coins, t: 'Ödeme incelemeleri', to: '/financial-reviews', show: canFinancial },
    { I: Icon.Settings, t: 'Bakım Modu', to: '/maintenance', show: canMaintenance },
    { I: Icon.Clock, t: 'Son aktiviteler', to: '/activity', show: canActivity },
  ].filter((q) => q.show);

  return (
    <div>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Genel Bakış</>}
        title="Genel Bakış"
        subtitle={
          <>
            Hoş geldin, <strong>{session.username ?? '—'}</strong>. Bu konsoldan müşterileri,
            siparişleri, sağlayıcıları ve sistem durumunu yönetirsin. Tüm işlemler API
            tarafında denetlenir ve aktivite akışına yazılır.
          </>
        }
      />

      {/* KPI bandı — her kartta sağ-üst köşe ikonu */}
      <div className="adm-section-title">Sağlayıcı Durumu (mail.com)</div>
      <div className="kpi-grid">
        <StatCard
          label="Satışa uygun alias"
          value={fmt(totalAvailable)}
          meta="Hemen satılabilir adres sayısı"
          icon={<Icon.Inbox size={14} />}
          iconTone="success"
          testId="kpi-available"
        />
        <StatCard
          label="Aktif kullanımda"
          value={fmt(totalLeased)}
          meta="Rezerve veya leased durumdaki alias"
          icon={<Icon.Activity size={14} />}
          iconTone="info"
          testId="kpi-leased"
        />
        <StatCard
          label="Toplam içe aktarılan"
          value={fmt(totalImported)}
          meta="İçe aktarılmış toplam alias"
          icon={<Icon.Server size={14} />}
          testId="kpi-imported"
        />
        <StatCard
          label="Sağlayıcı domain sayısı"
          value={canProvider ? inventory.length.toLocaleString('tr-TR') : '—'}
          meta="Aktif domain projeksiyonu"
          icon={<Icon.Globe size={14} />}
          testId="kpi-domains"
        />
      </div>

      {canProvider && inventoryQuery.kind === 'ok' && totalAvailable === 0 ? (
        <Banner tone="warn">
          Satışa uygun alias yok. mail.com Yönetimi sayfasından parent ekleyip alias içe
          aktarmanız gerekir.
        </Banner>
      ) : null}
      {inventoryQuery.kind === 'error' ? (
        <Banner tone="danger">
          Sağlayıcı envanteri yüklenemedi. Detay için{' '}
          <Link to="/mailcom-control-plane">mail.com Yönetimi</Link> sayfasına bakın.
        </Banner>
      ) : null}

      {/* 2-col: Hesap + Hızlı Erişim */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 4,
        }}
        className="dashboard-grid"
      >
        <Card flush>
          <CardHeader title="Hesap ve Yetkiler" actions={<RolePill role={adminRole} />} />
          <CardBody>
            <div className="dl-rows">
              <Row k="Kullanıcı" v={<strong>{session.username ?? '—'}</strong>} />
              <Row
                k="Aktör ID"
                v={session.actorId ? <IdMono value={session.actorId} /> : '—'}
              />
              <Row k="Oturum süresi" v={formatRelative(msLeft)} />
              <Row
                k="Yetki kapsamı"
                v={
                  (session.role as string) === 'superadmin' || (session.role as string) === 'super_admin' ? (
                    <Badge tone="info">Tüm sistem yönetimi</Badge>
                  ) : (
                    `${session.capabilities.length} yetki`
                  )
                }
              />
            </div>
            <details className="disclosure" style={{ marginTop: 12 }}>
              <summary>Teknik yetkileri göster</summary>
              <div className="dl-rows" style={{ marginTop: 8 }}>
                <Row k="Kaynak" v={session.origin ?? '—'} />
                <Row
                  k="Bitiş zamanı"
                  v={
                    <span className="t-mono t-xs">
                      {session.expiresAt ? session.expiresAt.toISOString() : '—'}
                    </span>
                  }
                />
                <Row
                  k="Yetki anahtarları"
                  v={
                    <span className="t-mono t-xs" style={{ wordBreak: 'break-word' }}>
                      {session.capabilities.length === 0 ? '(yok)' : session.capabilities.join(', ')}
                    </span>
                  }
                />
              </div>
            </details>
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader title="Hızlı Erişim" />
          <CardBody style={{ padding: 8 }}>
            {quickActions.length === 0 ? (
              <EmptyState label="Yetkilendirilmiş kısa yol yok." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {quickActions.map((q, i) => (
                  <Link
                    key={i}
                    to={q.to}
                    className="nav-item"
                    style={{ textDecoration: 'none' }}
                  >
                    <q.I size={14} />
                    <span style={{ flex: 1 }}>{q.t}</span>
                    <Icon.ChevronRight size={12} />
                  </Link>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Son Aktiviteler — icon-led liste (tablo DEĞİL) */}
      {canActivity ? (
        <div style={{ marginTop: 16 }}>
          <Card flush>
            <CardHeader
              title="Son Aktiviteler"
              actions={
                <Link to="/activity" style={{ textDecoration: 'none' }}>
                  <button className="btn btn-ghost btn-sm" type="button">
                    Tümü
                    <Icon.ArrowUR size={12} />
                  </button>
                </Link>
              }
            />
            <CardBody style={{ padding: 0 }}>
              {activityQuery.kind === 'loading' ? (
                <div style={{ padding: 20 }}>
                  <LoadingState />
                </div>
              ) : activityQuery.kind === 'error' ? (
                <div style={{ padding: 20 }}>
                  <ErrorState reason={activityQuery.reason} />
                </div>
              ) : activityQuery.kind === 'ok' && activityQuery.data.entries.length === 0 ? (
                <div style={{ padding: 20 }}>
                  <EmptyState label="Henüz aktivite yok." />
                </div>
              ) : activityQuery.kind === 'ok' ? (
                <ActivityList entries={activityQuery.data.entries.slice(0, 8)} />
              ) : null}
            </CardBody>
          </Card>
        </div>
      ) : null}

      {/* Yardım kutusu */}
      <div style={{ marginTop: 16 }}>
        <Banner tone="info">
          İhtiyacın olan sayfayı sol menüden bulabilirsin. Operasyonel runbook akışları
          operatör tarafında SSH ve audit ile yürütülüyor (R22.1, D-038).
        </Banner>
      </div>
    </div>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}

function ActivityList({ entries }: { readonly entries: readonly WireActivityEntry[] }): JSX.Element {
  const ICON_TONE_BG: Record<string, string> = {
    neutral: 'var(--bg-sunken)',
    warn: 'var(--warn-50)',
    danger: 'var(--danger-50)',
    success: 'var(--success-50)',
  };
  const ICON_TONE_FG: Record<string, string> = {
    neutral: 'var(--text-secondary)',
    warn: 'var(--warn-700)',
    danger: 'var(--danger-700)',
    success: 'var(--success-700)',
  };
  return (
    <div>
      {entries.map((entry, i) => {
        const any = entry as unknown as Record<string, unknown>;
        const kind = entry.kind;
        const at = typeof any['at'] === 'string' ? (any['at'] as string) : null;
        const actor =
          typeof any['actor'] === 'string'
            ? (any['actor'] as string)
            : typeof any['actorUsername'] === 'string'
            ? (any['actorUsername'] as string)
            : '—';
        const detail = describeActivityDetail(entry);
        const tone = toneForActivityKind(kind);
        const IconComp = iconForActivityKind(kind);
        return (
          <div
            key={`${at ?? 'na'}-${i}`}
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px 16px',
              borderBottom: i < entries.length - 1 ? '1px solid var(--border-subtle)' : '0',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: ICON_TONE_BG[tone],
                color: ICON_TONE_FG[tone],
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              <IconComp size={14} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 550, marginBottom: 2 }}>
                {ACTIVITY_KIND_LABEL[kind] ?? kind}
              </div>
              {detail ? (
                <div
                  className="t-xs t-tertiary"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--font-mono)',
                  }}
                  title={detail}
                >
                  {detail}
                </div>
              ) : null}
              <div
                className="t-xs t-tertiary"
                style={{ marginTop: 3, fontFamily: 'var(--font-mono)' }}
              >
                {actor} · {ago(at)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function describeActivityDetail(entry: WireActivityEntry): string {
  const any = entry as unknown as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ['targetActorId', 'orderId', 'providerId', 'parentId', 'reason']) {
    const v = any[key];
    if (typeof v === 'string' && v.length > 0) parts.push(`${key}=${v}`);
  }
  return parts.join(' · ');
}
